import dayjs, { ConfigType } from 'dayjs'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IClientSetup } from 'src/app/shared/models/clientSetup.model'
import * as yup from 'yup'
import { FORMAT_DATE } from '../../../../shared/constants/app.constants'
import { getGlobalExceptionDefaults } from 'src/app/shared/services/SystemWideRulesService'

// The 3 exception fields (reminder + Check/ACH cutoff) come from the global System-Wide Rules config.
// Shared by Add/Edit/View Client Setup: each screen starts these fields empty and then populates them
// from getGlobalExceptionDefaults() (Edit/View merge the result into the loaded data; Add uses setValue).
export const fetchGlobalExceptionDefaults = async () => {
  try {
    const res = await getGlobalExceptionDefaults()
    const global = res?.data
    if (!global) return null
    return {
      exceptionReminderEmailTimeInMinutes: global.defaultExceptionEmailReminder,
      defaultCheckExceptionCutoffTime: normalizeCutoffTime(global.defaultCheckExceptionCutoffTime),
      defaultAchExceptionCutoffTime: normalizeCutoffTime(global.defaultAchExceptionCutoffTime),
      timeZoneId: global.timeZoneId,
    }
  } catch (error) {
    console.log('ERROR: ', error)
    return null
  }
}

export const applyGlobalExceptionDefaults = async (setValue: (name: any, value: any) => void) => {
  const values = await fetchGlobalExceptionDefaults()
  if (!values) return
  setValue('exceptionReminderEmailTimeInMinutes', values.exceptionReminderEmailTimeInMinutes)
  setValue('defaultCheckExceptionCutoffTime', values.defaultCheckExceptionCutoffTime)
  setValue('defaultAchExceptionCutoffTime', values.defaultAchExceptionCutoffTime)
}

function getCutoffTimeList(minuteRange: number) {
  const times = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += minuteRange) {
      // Pad hours and minutes with zeros for consistent formatting
      const hourString = hour.toString().padStart(2, '0')
      const minuteString = minute.toString().padStart(2, '0')
      const timeString = `${hourString}:${minuteString}:00`

      // Convert to 12-hour format and determine AM/PM
      const hour12 = hour % 12 || 12 // Handle 0 hour as 12 AM
      const amPm = hour < 12 ? 'AM' : 'PM'
      const formattedTime = `${hour12}:${minuteString} ${amPm}`

      times.push({
        value: timeString,
        label: formattedTime,
      })
    }
  }
  return times
}

function generateValidationSchema(t: any) {
  const validationSchema = yup.object().shape({
    clientName: yup.string().required(t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_CLIENT_NAME)),
    clientNumber: yup.string().required(t('ClientSetup:addNewClient.validation.clientIdRequired')),
    // exceptionReminderEmailTimeInMinutes, defaultCheckExceptionCutoffTime and defaultAchExceptionCutoffTime
    // are now managed globally via System-Wide Rules and shown view-only, so they are intentionally NOT
    // validated here (they are not user-editable fields anymore).
    defaultCheckExceptionCreationType: yup.string().required(),
    issuedCheckFileFormats: yup.array().when('defaultCheckExceptionCreationType', (val) => {
      // if (val[0] === 'CREATE') {
      //     return yup.array().min(
      //         1,
      //         t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
      //             Required_Field: `${t('ClientSetup:addNewClient.label.issuedCheckFileFormats')}`,
      //         }),
      //     )
      // }
      return yup.array().notRequired()
    }),
    autoLoadIssuedCheckFiles: yup.boolean(),
    sftpFileLocation: yup.string().when('autoLoadIssuedCheckFiles', (val) => {
      if (val[0]) {
        return yup.string().required(
          t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
            Required_Field: `SFTP File Location`,
          }),
        )
      }
      return yup.string().notRequired()
    }),
    payeeMatch: yup.boolean(),
    payeeMatchParameterId: yup.string().when('payeeMatch', (val) => {
      if (val[0]) {
        return yup.string().required(
          t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
            Required_Field: `Payee Match Parameter`,
          }),
        )
      }
      return yup.string().notRequired()
    }),
    defaultAchExceptionCreationType: yup.string().required(),
  })
  .test(
      'at-least-one-exception-create',
      'At least one exception type (ACH or Check) must be enabled to proceed.',
      function (values) {
        const { defaultAchExceptionCreationType, defaultCheckExceptionCreationType } = values || {}

        const isAchNoCreate = defaultAchExceptionCreationType === 'NOCREATE'
        const isCheckNoCreate = defaultCheckExceptionCreationType === 'NOCREATE'

        return !(isAchNoCreate && isCheckNoCreate)
      },
    )
  return validationSchema
}

function mappingDataToForm(data: any) {
  const {
    staleDatedCheck,
    id,
    // Client Info
    clientName,
    clientNumber,
    referenceCode,
    clientNotes,
    timeZoneId,
    exceptionReminderEmailTimeInMinutes,
    // Check Exceptions
    defaultCheckExceptionCreationType,
    defaultCheckExceptionCutoffTime,
    defaultCheckDecision,
    defaultCheckReason,
    issuedCheckFileFormats,
    displayIssuedPayee,
    payeeMatch,
    payeeMatchParameterId,
    displayNotes,
    autoLoadIssuedCheckFiles,
    sftpFileLocation,
    // ACH Exceptions
    defaultAchExceptionCreationType,
    defaultAchExceptionCutoffTime,
    defaultAchDecision,
    defaultAchReason,
    achAuthorizationRulesNotificationType,
    // Archive when editing
    archived,
    activationDate,
  } = data

  const activationDateDayjs = activationDate ? dayjs(activationDate) : null

  return {
    staleDatedCheck,
    id,
    // Client Info
    clientName,
    clientNumber,
    referenceCode,
    clientNotes,
    timeZoneId,
    exceptionReminderEmailTimeInMinutes,
    // Check Exceptions
    defaultCheckExceptionCreationType,
    defaultCheckExceptionCutoffTime,
    defaultCheckDecision,
    defaultCheckReason,
    issuedCheckFileFormats,
    displayIssuedPayee,
    payeeMatch,
    payeeMatchParameterId,
    displayNotes,
    autoLoadIssuedCheckFiles,
    sftpFileLocation,
    // ACH Exceptions
    defaultAchExceptionCreationType,
    defaultAchExceptionCutoffTime,
    defaultAchDecision,
    defaultAchReason,
    achAuthorizationRulesNotificationType,
    // Archive when editing
    archived,
    activationDate: activationDateDayjs,
  }
}

// Normalizes a cutoff time from the global config (e.g. "11:30:00.0000000") to the
// Select option value format "HH:mm:00".
export const normalizeCutoffTime = (time?: string): string | undefined => {
  if (!time) return time
  const [hour, minute] = time.split(':')
  if (hour === undefined || minute === undefined) return time
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00`
}

const formatDataBeforeSubmit = (data: IClientSetup, sourceId?: string, disableActivationDate?: boolean) => {
  const payload: any = {
    ...data,
    sourceId,
    clientName: data?.clientName.trim(),
    referenceCode: data?.referenceCode.trim(),
    activationDate: disableActivationDate ? null : data?.activationDate,
  }
  // exceptionReminderEmailTimeInMinutes / cutoff times are managed globally via System-Wide Rules and shown
  // view-only. They are not user-editable, but the current global values (already loaded into the form) are
  // still sent so the payload stays valid for the API — the user never edits them.
  payload.defaultCheckExceptionCutoffTime = normalizeCutoffTime(payload.defaultCheckExceptionCutoffTime)
  payload.defaultAchExceptionCutoffTime = normalizeCutoffTime(payload.defaultAchExceptionCutoffTime)
  return payload
}

const convertTime = ({ timezone, date, format }: { date: ConfigType; timezone?: string; format?: string }) => {
  return dayjs
    .utc(date)
    .tz(timezone)
    .format(format || FORMAT_DATE)
}

const RESET_FORM_FIELDS = {
  CHECK_EXCEPTIONS: [
    'defaultCheckDecision',
    'defaultCheckReason',
    'issuedCheckFileFormats',
    'displayIssuedPayee',
    'payeeMatch',
    'payeeMatchParameterId',
    'displayNotes',
    'autoLoadIssuedCheckFiles',
    'sftpFileLocation',
  ],
  ACH_CHECK_CHECK_EXCEPTIONS: ['defaultAchDecision', 'defaultAchReason', 'achAuthorizationRulesNotificationType'],
}

export {
  convertTime,
  formatDataBeforeSubmit,
  generateValidationSchema,
  getCutoffTimeList,
  mappingDataToForm,
  RESET_FORM_FIELDS,
}
